import React, { useLayoutEffect, useRef, useState } from "react";
import "./MeasurementDownloads.css";

const CATALOG_PATH = "/api/v1/downloads/measurement-rev4";
const IDS = Array.from({ length: 9 }, (_, index) => `rev4-print-0${index + 1}`);
const LICENSE_LABEL = "Persönlicher, nichtkommerzieller Gebrauch";
const LICENSE_SCOPE = "Nur die neun freigegebenen STL-Druckteile; keine Weitergabe von Kaufteil- oder Referenzmodellen.";
const keysAre = (value, keys) => value && typeof value === "object" && !Array.isArray(value)
  && Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));

function catalogFiles(document) {
  if (!keysAre(document, ["release_id", "license", "artifacts"])
    || document.release_id !== "measurement-rev4"
    || !keysAre(document.license, ["label", "scope"])
    || document.license.label !== LICENSE_LABEL || document.license.scope !== LICENSE_SCOPE
    || !Array.isArray(document.artifacts) || document.artifacts.length !== 9) throw new Error("catalog_invalid");
  const files = document.artifacts.map(file => {
    if (!keysAre(file, ["id", "filename", "media_type", "size_bytes", "sha256", "download_path"])
      || !IDS.includes(file.id) || typeof file.filename !== "string" || file.filename.length > 128
      || /^[A-Za-z0-9][A-Za-z0-9_.-]*\.stl$/.exec(file.filename)?.[0] !== file.filename
      || file.media_type !== "model/stl" || !Number.isSafeInteger(file.size_bytes)
      || file.size_bytes < 1 || file.size_bytes > 8388608 || typeof file.sha256 !== "string"
      || /^[0-9a-f]{64}$/.exec(file.sha256)?.[0] !== file.sha256
      || file.download_path !== `${CATALOG_PATH}/${file.id}`) throw new Error("catalog_invalid");
    return { id: file.id, filename: file.filename, bytes: file.size_bytes, path: file.download_path };
  });
  if (new Set(files.map(file => file.id)).size !== 9
    || new Set(files.map(file => file.filename)).size !== 9) throw new Error("catalog_invalid");
  return files.sort((first, second) => first.id.localeCompare(second.id));
}

const MESSAGE = {
  auth: "Bitte melde dich mit deinem Kundenkonto an, um die Druckdateien abzurufen.",
  password: "Bitte schließe zuerst den erforderlichen Passwortwechsel in deinem Kundenkonto ab.",
  unavailable: "Die Druckdateien sind derzeit noch nicht verfügbar. Bitte versuche es später erneut.",
  timeout: "Die Prüfung hat zu lange gedauert. Bitte versuche es erneut.",
};

// scopeRef is only a local view-generation boundary, never an authentication
// credential. The server checks the cookie again for EVERY file download.
export default function MeasurementDownloads({ scopeRef = null }) {
  const request = useRef({ mounted: false, epoch: 0, pending: false, controller: null, timer: null });
  const [view, setView] = useState(null);
  useLayoutEffect(() => {
    const owner = request.current;
    owner.mounted = true;
    owner.scope = scopeRef;
    owner.epoch += 1;
    setView(null);
    const cancel = () => {
      owner.epoch += 1;
      owner.controller?.abort();
      owner.controller = null;
      owner.pending = false;
      clearTimeout(owner.timer);
      owner.timer = null;
    };
    const expired = () => {
      cancel();
      setView({ scope: scopeRef, epoch: owner.epoch, status: "error", message: "auth" });
    };
    window.addEventListener("feeder-session-expired", expired);
    return () => {
      owner.mounted = false;
      cancel();
      window.removeEventListener("feeder-session-expired", expired);
    };
  }, [scopeRef]);

  const load = async () => {
    const owner = request.current;
    if (!owner.mounted || owner.pending || owner.scope !== scopeRef) return;
    const controller = new AbortController(), epoch = ++owner.epoch;
    const identity = { scope: scopeRef, epoch };
    owner.pending = true;
    owner.controller = controller;
    const current = () => owner.mounted && owner.scope === scopeRef && owner.epoch === epoch
      && owner.controller === controller && !controller.signal.aborted;
    setView({ ...identity, status: "loading" });
    owner.timer = setTimeout(() => {
      if (!current()) return;
      controller.abort();
      owner.controller = null;
      owner.pending = false;
      owner.timer = null;
      setView({ ...identity, status: "error", message: "timeout" });
    }, 12000);
    try {
      const response = await fetch(CATALOG_PATH, {
        method: "GET", credentials: "same-origin", cache: "no-store", redirect: "error",
        headers: { Accept: "application/json" }, signal: controller.signal,
      });
      if (!current()) return;
      if (response.redirected || response.status !== 200 || !response.ok) {
        const message = response.status === 401 ? "auth" : response.status === 403 ? "password" : "unavailable";
        setView({ ...identity, status: "error", message });
        return;
      }
      if (response.headers?.get("Content-Type")?.split(";")[0].trim().toLowerCase() !== "application/json") {
        throw new Error("catalog_invalid");
      }
      const document = await response.json();
      if (!current()) return;
      const files = catalogFiles(document);
      setView({ ...identity, status: "loaded", files });
    } catch (_) {
      if (current()) setView({ ...identity, status: "error", message: "unavailable" });
    } finally {
      if (current()) {
        clearTimeout(owner.timer);
        owner.timer = null;
        owner.pending = false;
        owner.controller = null;
      }
    }
  };

  const active = view?.scope === scopeRef && view?.epoch === request.current.epoch ? view : null;
  return <section className="feeder-measurement-downloads" aria-label="Druckdateien der Messeinrichtung" aria-busy={active?.status === "loading"}>
    <h3>Druckteile für deine Messeinrichtung</h3>
    <p>Neun STL-Dateien · Rev4 · {LICENSE_LABEL}. Die physische Passung und der Einsatz in deinem Aufbau sind nicht geprüft.</p>
    <button type="button" disabled={active?.status === "loading"} onClick={load}>
      {active ? "Druckdateien erneut prüfen" : "Druckdateien mit Kundenkonto laden"}
    </button>
    {active?.status === "loading" && <p role="status">Kundenzugang und Dateikatalog werden geprüft …</p>}
    {active?.status === "error" && <p role="alert">{MESSAGE[active.message]}{["auth", "password"].includes(active.message) && <> <a href="/anmelden">Zum Kundenkonto anmelden</a></>}</p>}
    {active?.files && <>
      <ul>{active.files.map(file => <li key={file.id}>
        <a href={file.path}>{file.filename}</a>
        <span>{new Intl.NumberFormat("de-AT", { maximumFractionDigits: 2 }).format(file.bytes / 1048576)} MiB · STL</span>
      </li>)}</ul>
      <p>{LICENSE_SCOPE} Jeder Dateiabruf wird erneut serverseitig für dein Kundenkonto geprüft.</p>
    </>}
  </section>;
}
